'use client';

import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { skipOnboarding } from '@/lib/onboarding';

/**
 * "Skip for now" escape hatch shown in the footer of the first setup step. Confirms
 * before leaving, then clears the onboarding intent and drops the merchant on the org
 * dashboard. Self-contained: owns its own navigation so the step stays presentational.
 */
export function SetupSkipControl() {
  const { t } = useTranslation('setup');
  const { navigate, orgSlug } = useOrgNavigate();

  const handleSkip = () => {
    navigate(skipOnboarding({ orgSlug }));
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground -ml-2 h-8 gap-1 px-2.5 text-[0.8125rem] font-semibold"
        >
          {t('experience.skip.action')}
          <ChevronRight className="size-3.5" strokeWidth={2} aria-hidden />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('experience.skip.dialog.title')}</AlertDialogTitle>
          <AlertDialogDescription>{t('experience.skip.dialog.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('experience.skip.dialog.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={handleSkip}>{t('experience.skip.dialog.confirm')}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
